import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const partyColor = (p) => (p?.toLowerCase().startsWith("dem") ? "bg-brightblue" : "bg-donate");

export const ResultsBoard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/results").then((r) => setData(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const rows = data ? [...data.candidates].sort((a, b) => b.votes - a.votes) : [];
  const max = Math.max(1, ...rows.map((r) => r.votes));
  const pm = data?.precincts?.match(/([\d,]+) of ([\d,]+)/);
  const reported = pm ? Number(pm[1].replace(/,/g, "")) : 0;
  const totalPrecincts = pm ? Number(pm[2].replace(/,/g, "")) : 0;

  return (
    <div className="text-left" data-testid="results-board">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-widest text-blue-200">House of Delegates · District 7A · Vote for 2</div>
        <span className="flex items-center gap-1.5 text-[11px] font-bold text-white bg-donate rounded-full px-2.5 py-1" data-testid="results-live-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE
        </span>
      </div>
      {totalPrecincts > 0 && (
        <div className="mb-4" data-testid="precinct-progress">
          <div className="flex justify-between text-[11px] font-semibold text-blue-100 mb-1.5">
            <span>Election day precincts reported</span>
            <span data-testid="precinct-progress-text">{reported} of {totalPrecincts}</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-white transition-[width] duration-700" style={{ width: `${Math.min(100, (reported / totalPrecincts) * 100)}%` }} />
          </div>
        </div>
      )}
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.name} className="rounded-xl bg-white/10 border border-white/15 p-3" data-testid={`result-row-${r.name.split(" ").pop().toLowerCase()}`}>
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-white font-semibold">{r.name} <span className="text-blue-200 text-xs font-normal">({r.party})</span></div>
              <div className="text-white font-serif font-extrabold tabular-nums">{r.votes.toLocaleString()} <span className="text-blue-200 text-xs font-sans font-semibold">{r.percent}%</span></div>
            </div>
            <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className={`h-full ${partyColor(r.party)} transition-[width] duration-700`} style={{ width: `${(r.votes / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-blue-200/80" data-testid="results-meta">
        {data?.source === "none" && "Waiting for the first official count. Numbers update automatically."}
        {data?.source === "official" && `Source: Maryland State Board of Elections${data.precincts ? ` · ${data.precincts}` : ""}`}
        {data?.source === "manual" && "Unofficial count reported by the campaign."}
        {data?.updated_at && data.source !== "none" && ` · Updated ${new Date(data.updated_at).toLocaleTimeString()}`}
      </p>
      {data?.thank_you_note && (
        <div className="mt-5 rounded-2xl bg-white/10 border border-white/15 p-4 text-left" data-testid="results-thank-you-note">
          <p className="text-white text-sm leading-relaxed whitespace-pre-line">{data.thank_you_note}</p>
          <p className="mt-2 text-blue-200 text-xs font-semibold">— Satish Chapagain</p>
        </div>
      )}
    </div>
  );
};
